import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LogIn, LogOut, AlertTriangle, Plus, Trash2, Loader2, Save, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

export const MovementBadge = ({ m }) => {
  if (m.flag === "sortie_anticipee_non_autorisee")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3" />Sortie NON autorisée</span>;
  if (m.type === "entree")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><LogIn className="h-3 w-3" />Entrée</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700"><LogOut className="h-3 w-3" />Sortie{m.flag === "sortie_anticipee_autorisee" ? " (autorisée)" : ""}</span>;
};

// ---------- Journal ----------
export const JournalTab = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);

  const load = async (d) => {
    try { const { data } = await api.get("/gate/movements", { params: { date: d } }); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(date); }, [date]);

  return (
    <div className="space-y-4" data-testid="journal-tab">
      <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="journal-date" />
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Heure</th>
              <th className="px-4 py-2 text-left">Élève / Personnel</th>
              <th className="px-4 py-2 text-left">Classe</th>
              <th className="px-4 py-2 text-left">Mouvement</th>
              <th className="px-4 py-2 text-left">Agent</th>
              <th className="px-4 py-2 text-left">Notif.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(m => (
              <tr key={m.id} className={`border-t border-slate-100 ${m.flag === "sortie_anticipee_non_autorisee" ? "bg-red-50" : ""}`} data-testid={`movement-${m.id}`}>
                <td className="px-4 py-2 font-mono text-xs font-bold text-[#1E3A8A]">{fmtTime(m.timestamp)}</td>
                <td className="px-4 py-2 font-semibold">
                  {m.student_name}
                  {m.staff
                    ? <span className="text-[10px] font-bold uppercase bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5 ml-1.5">Personnel</span>
                    : <span className="text-xs text-slate-400 ml-1">{m.matricule}</span>}
                </td>
                <td className="px-4 py-2 text-slate-600">{m.class_name}</td>
                <td className="px-4 py-2"><MovementBadge m={m} /></td>
                <td className="px-4 py-2 text-xs text-slate-500">{m.scanned_by_name}</td>
                <td className="px-4 py-2 text-xs">
                  {m.notified?.email && <Mail className="h-3.5 w-3.5 inline text-green-600 mr-1" />}
                  {m.notified?.sms && <MessageSquare className="h-3.5 w-3.5 inline text-green-600" />}
                  {!m.notified?.email && !m.notified?.sms && <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aucun mouvement ce jour.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Sorties anticipées ----------
export const EarlyExitsTab = () => {
  const { user } = useAuth();
  const canCreate = ["admin", "super_admin", "director", "scolarite"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ student_id: "", date: new Date().toISOString().slice(0, 10), reason: "", picked_up_by: "" });
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/gate/early-exits"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => {
    load();
    if (canCreate) api.get("/students").then(({ data }) => setStudents(data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.student_id || !form.reason.trim()) return toast.error("Élève et motif obligatoires");
    setSaving(true);
    try {
      await api.post("/gate/early-exits", form);
      toast.success("Autorisation créée");
      setDlg(false);
      setForm({ ...form, student_id: "", reason: "", picked_up_by: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Supprimer l'autorisation de ${r.student_name} ?`)) return;
    try { await api.delete(`/gate/early-exits/${r.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = students.filter(s =>
    `${s.first_name} ${s.last_name} ${s.matricule}`.toLowerCase().includes(search.toLowerCase())).slice(0, 30);

  return (
    <div className="space-y-4" data-testid="early-exits-tab">
      {canCreate && (
        <Button onClick={() => setDlg(true)} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-early-exit-btn">
          <Plus className="h-4 w-4" />Autoriser une sortie anticipée
        </Button>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Élève</th>
              <th className="px-4 py-2 text-left">Motif</th>
              <th className="px-4 py-2 text-left">Récupéré par</th>
              <th className="px-4 py-2 text-left">Autorisé par</th>
              <th className="px-4 py-2 text-left">Statut</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`early-exit-${r.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.date}</td>
                <td className="px-4 py-2 font-semibold">{r.student_name}<span className="text-xs text-slate-400 ml-1">{r.class_name}</span></td>
                <td className="px-4 py-2 text-slate-600 max-w-[200px] truncate">{r.reason}</td>
                <td className="px-4 py-2 text-slate-600">{r.picked_up_by || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{r.authorized_by_name}</td>
                <td className="px-4 py-2">
                  {r.used
                    ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Utilisée</span>
                    : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  {canCreate && !r.used && <button onClick={() => del(r)} className="text-red-400 hover:text-red-600" data-testid={`delete-early-exit-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Aucune autorisation de sortie anticipée.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="early-exit-dialog">
          <DialogHeader><DialogTitle>Autoriser une sortie anticipée</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rechercher l&apos;élève</Label>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom ou matricule..." data-testid="early-exit-search" />
              <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
                <SelectTrigger className="mt-2" data-testid="early-exit-student"><SelectValue placeholder="Choisir l'élève" /></SelectTrigger>
                <SelectContent>
                  {filtered.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.class_name} ({s.matricule})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="early-exit-date" />
            </div>
            <div>
              <Label>Motif *</Label>
              <Textarea rows={2} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Rendez-vous médical..." data-testid="early-exit-reason" />
            </div>
            <div>
              <Label>Récupéré par (optionnel)</Label>
              <Input value={form.picked_up_by} onChange={e => setForm({ ...form, picked_up_by: e.target.value })} placeholder="Nom du parent / tuteur" data-testid="early-exit-pickup" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="early-exit-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Autoriser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Paramètres ----------
export const GateSettingsTab = () => {
  const { user } = useAuth();
  const canEdit = ["admin", "super_admin", "director"].includes(user?.role);
  const [s, setS] = useState(null);
  const [bio, setBio] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/gate/settings").then(({ data }) => setS(data)).catch(() => {});
    api.get("/biometric/settings").then(({ data }) => setBio(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/gate/settings", { school_end_time: s.school_end_time, notify_email: s.notify_email, notify_sms: s.notify_sms });
      if (bio) await api.put("/biometric/settings", { require_biometric: !!bio.require_biometric });
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!s) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-xl space-y-5" data-testid="gate-settings-tab">
      <div>
        <Label className="font-semibold">Heure de fin des cours</Label>
        <Input type="time" value={s.school_end_time} onChange={e => setS({ ...s, school_end_time: e.target.value })} className="w-32 mt-1" disabled={!canEdit} data-testid="setting-end-time" />
        <p className="text-xs text-slate-500 mt-1">Toute sortie avant cette heure est considérée comme anticipée (autorisation requise).</p>
      </div>
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={s.notify_email} onChange={e => setS({ ...s, notify_email: e.target.checked })} disabled={!canEdit} data-testid="setting-notify-email" />
        Notifier les parents par email (Resend)
      </label>
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={s.notify_sms} onChange={e => setS({ ...s, notify_sms: e.target.checked })} disabled={!canEdit} data-testid="setting-notify-sms" />
        Notifier les parents par SMS (Twilio — compte trial : envois bloqués tant que non upgradé)
      </label>
      {bio && (
        <label className="flex items-center gap-2 text-sm font-medium border-t border-slate-100 pt-4">
          <input type="checkbox" checked={!!bio.require_biometric} onChange={e => setBio({ ...bio, require_biometric: e.target.checked })} disabled={!canEdit} data-testid="setting-require-biometric" />
          Exiger la vérification biométrique (empreinte / Face ID) au pointage du personnel enrôlé
        </label>
      )}
      {canEdit && (
        <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white gap-1" data-testid="gate-settings-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" />Enregistrer</>}
        </Button>
      )}
    </div>
  );
};
